/**
 * Lo que Gavi añade a cada fila de «Pagos fijos»: cada comercio por
 * separado cuando una categoría junta varios (Netflix y Spotify en
 * «Suscripciones») y la píldora dorada «Sube» (o, en luz y gas, «+18 % que
 * hace un año», porque pagar más puede ser gastar más). Funciones puras.
 */
import { diasEntre } from '#components/mobile/budget/pagosFijos';

import { comparaConHaceUnAnio, subidaDe, subidaNotable } from './cobros';
import type { SerieCobro } from './cobros';
import { tipoDeSerie } from './consejos';
import type { Contrato } from './estadoGavi';
import { esConsumo } from './tiposRecibo';

/** Una subida cuenta en la fila durante tres meses. */
const DIAS_SUBIDA_RECIENTE = 90;

export type ComercioFila = {
  clave: string;
  comercio: string;
  importe: number;
  /** Céntimos que ha subido hace poco, o null. */
  sube: number | null;
};

export type ExtraFila = {
  /** Con dos comercios o más; si no, vacío. */
  desglose: ComercioFila[];
  /** Suma de lo que ha subido hace poco (céntimos), o null. */
  sube: number | null;
  /** Luz y gas: % más que hace un año (redondeado), si es ≥ 10. */
  interanual: number | null;
};

export function subidaReciente(serie: SerieCobro, hoy: string): number | null {
  const s = subidaDe(serie);
  if (!s || !subidaNotable(s)) {
    return null;
  }
  const limite = serie.cada === 12 ? 365 : DIAS_SUBIDA_RECIENTE;
  return diasEntre(s.fecha, hoy) <= limite ? s.ahora - s.antes : null;
}

export function extraFila(
  series: readonly SerieCobro[],
  nombreCategoria: string | null,
  contrato: Contrato | undefined,
  hoy: string,
): ExtraFila | null {
  const principal = series[0];
  if (!principal) {
    return null;
  }
  const tipo = tipoDeSerie(principal, nombreCategoria, contrato);
  if (tipo === 'otro') {
    return null;
  }
  if (esConsumo(tipo)) {
    const i = comparaConHaceUnAnio(principal, hoy);
    const pct = i ? Math.round(i.cambio * 100) : 0;
    return { desglose: [], sube: null, interanual: pct >= 10 ? pct : null };
  }
  const desglose = series.map(s => ({
    clave: s.clave,
    comercio: s.comercio,
    importe: s.importe,
    sube: subidaReciente(s, hoy),
  }));
  const subidas = desglose.filter(d => d.sube != null);
  return {
    desglose: series.length >= 2 ? desglose : [],
    sube:
      subidas.length > 0
        ? subidas.reduce((t, d) => t + (d.sube ?? 0), 0)
        : null,
    interanual: null,
  };
}
